import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import type { FocusEvent, PointerEvent, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ChevronDown,
  ChevronRight,
  Command,
  LogOut,
  Menu,
  Pin,
  PinOff,
  Search,
  X,
} from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { initials } from '@/lib/format';
import {
  MODULES,
  NAV_GROUPS,
  canOpen,
  childForPath,
  moduleForPath,
  type NavChild,
  type NavModule,
} from '@/navigation';
import { CommandPalette } from '@/components/CommandPalette';
import { SearchContext } from './search-context';

/**
 * Shell do AMAI OPS: barra lateral, barra de topo e o workspace.
 *
 * A barra lateral é BRANCA e agrupada por natureza do trabalho (Operação,
 * Comercial, Administrativo, Sistema). A barra de topo tem a trilha de onde
 * se está, a busca e o perfil — e nada mais: o título e as ações da tela
 * moram no cabeçalho da própria página.
 *
 * A barra tem TRÊS estados, e a diferença entre eles é o que o layout faz:
 *
 * - recolhida (padrão): 72px de trilho só com ícones; o conteúdo começa logo
 *   depois;
 * - aberta por passagem do mouse: abre POR CIMA do conteúdo, sem empurrar a
 *   página — quem só quer conferir um nome não vê a tabela inteira dançar;
 * - fixada pelo operador: o layout RESERVA a largura dela, porque aí a barra
 *   virou parte da tela e não uma consulta rápida.
 *
 * A largura antiga era 208px fixos. Numa tela de contas a pagar isso é uma
 * coluna inteira de tabela gasta com um menu que, na maior parte do dia,
 * ninguém está olhando.
 */
const PIN_KEY = 'amai:sidebar-pinned';
/** Chave do estado antigo (recolhida sim/não), lida uma vez para migrar. */
const LEGACY_COLLAPSE_KEY = 'amai:sidebar-collapsed';
/**
 * A preferência de tema dos reboots escuros. O produto agora é só claro;
 * a chave é apagada na primeira abertura para não sobrar lixo no navegador
 * de quem tinha escolhido o escuro.
 */
const OLD_THEME_KEY = 'amai:theme';

/**
 * Módulo que ainda não tem uma tela sequer.
 *
 * Estes SAEM da lista e viram uma linha só no rodapé do menu. Antes ficavam
 * ali, apagados, com um selo "breve" — sete dos dezesseis itens. Metade do
 * menu anunciava o que não existe, dois nem cabiam na largura ("Estacioname…",
 * "Site & Conteú…"), e o sistema inteiro passava a impressão de demonstração.
 * Quem opera precisa achar rápido o que dá para usar hoje.
 */
function emPreparo(module: NavModule): boolean {
  if (module.soon) return true;
  if (module.to) return false;
  return (module.children ?? []).every((child) => child.soon);
}

/**
 * Preferência de quem usa, lida ANTES do primeiro desenho.
 *
 * É de propósito que isto rode no inicializador do `useState` e não num
 * efeito: ler depois faria a barra nascer recolhida e pular para fixada no
 * quadro seguinte, justamente o "flash" que o layout inteiro tenta evitar.
 *
 * Falha em silêncio (navegador com armazenamento bloqueado): o padrão é a
 * barra recolhida, que é o comportamento mais conservador — nunca esconde
 * conteúdo que o operador esperava ver.
 */
function readPinned(): boolean {
  try {
    const stored = localStorage.getItem(PIN_KEY);
    if (stored !== null) return stored === '1';
    // Quem já tinha deixado a barra aberta no modelo antigo continua com ela
    // aberta: a preferência era a mesma, só mudou o nome.
    return localStorage.getItem(LEGACY_COLLAPSE_KEY) === '0';
  } catch {
    return false;
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [pinned, setPinned] = useState(readPinned);
  /** Mouse em cima da barra. Some quando o ponteiro sai. */
  const [hovered, setHovered] = useState(false);
  /** Foco de teclado dentro da barra: quem navega por Tab também precisa ler. */
  const [focused, setFocused] = useState(false);
  /** Gaveta do celular e do tablet, onde não existe "passar o mouse". */
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeModule = moduleForPath(location.pathname);
  /**
   * Um grupo aberto por vez, e quem escolhe qual é a ROTA — não um estado à
   * parte. Guardar "qual grupo está aberto" em `useState` exigia um efeito
   * para sincronizar a cada navegação, e efeito que chama `setState` é um
   * render a mais depois da tela já estar pronta: o grupo abria no quadro
   * seguinte, piscando. Derivar da rota acerta no primeiro desenho.
   *
   * O que a rota sozinha NÃO dava: clicar no módulo já aberto para recolher.
   * Isso é estado de gente, e vive aqui — amarrado à TELA em que o operador
   * clicou. Sair daquela tela invalida a escolha e o grupo volta a abrir, que
   * é o certo: chegar numa seção com o grupo dela fechado seria mentira sobre
   * onde se está. E, por ser comparação e não efeito, continua sem piscar.
   */
  const [recolhidoNaMao, setRecolhidoNaMao] = useState<{ id: string; path: string } | null>(null);

  const openModuleId =
    recolhidoNaMao?.id === activeModule?.id && recolhidoNaMao?.path === location.pathname
      ? null
      : (activeModule?.id ?? null);

  useEffect(() => {
    try {
      localStorage.setItem(PIN_KEY, pinned ? '1' : '0');
    } catch {
      // Navegador com armazenamento bloqueado: só perde a preferência.
    }
  }, [pinned]);

  useEffect(() => {
    delete document.documentElement.dataset.theme;
    try {
      localStorage.removeItem(OLD_THEME_KEY);
    } catch {
      // Armazenamento bloqueado: não havia o que limpar.
    }
  }, []);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      // Esc devolve a tela: fecha a gaveta e desfaz a abertura temporária.
      // Não desfixa — isso é escolha do operador, não acidente de teclado.
      if (event.key === 'Escape') {
        setDrawerOpen(false);
        setHovered(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const visibleChildren = useCallback(
    (children_: NavChild[] | undefined) =>
      (children_ ?? []).filter((child) => canOpen(can, child.permission)),
    [can],
  );

  // Módulo que ainda não existe não aparece — nem apagado, nem como a linha
  // "6 módulos em preparo" no rodapé. Roadmap do software na barra de toda
  // tela faz a ferramenta parecer demonstração, e o operador não tem o que
  // fazer com essa informação. Some também o módulo cujas seções este perfil
  // não abre.
  /** Seções que este perfil abre de fato — as previstas não contam. */
  const abriveis = useCallback(
    (children_: NavChild[] | undefined) =>
      visibleChildren(children_).filter((child) => !child.soon),
    [visibleChildren],
  );

  const modulosNoMenu = useMemo(
    () =>
      MODULES.filter(
        (module) =>
          !emPreparo(module) && (Boolean(module.to) || abriveis(module.children).length > 0),
      ),
    [abriveis],
  );

  const perfil = user?.profiles.map((profile) => profile.name).join(' · ') ?? '';
  const secaoAtual = childForPath(activeModule, location.pathname);
  const trilha = [activeModule?.label, secaoAtual?.label].filter(
    (parte, i, todas): parte is string => Boolean(parte) && todas.indexOf(parte) === i,
  );
  const searchValue = useMemo(() => ({ openSearch: () => setPaletteOpen(true) }), []);

  /**
   * Aberta é o OU de quatro coisas, e cada uma tem um dono diferente: o
   * operador (fixou), o mouse, o teclado e a gaveta do celular.
   */
  const expanded = pinned || hovered || focused || drawerOpen;
  /** Aberta sem estar fixada: flutua sobre o conteúdo em vez de empurrá-lo. */
  const flutuando = expanded && !pinned;

  // Só mouse abre por passagem. Em tela de toque, `pointerenter` dispara no
  // toque e a barra abriria sozinha em cima do que o operador quis tocar.
  const onPointerEnter = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse') setHovered(true);
  };

  /**
   * Só o foco de TECLADO segura a barra aberta.
   *
   * Clicar num botão também dá foco a ele. Com a regra antiga — "qualquer foco
   * dentro da barra mantém aberta" — bastava clicar no pin para a barra ficar
   * presa: o foco continuava no botão, o mouse ia embora, e ela não recolhia
   * mais. Era o que o Diretor estava vendo.
   *
   * `:focus-visible` é exatamente essa distinção, e quem decide é o navegador:
   * ele acende para Tab e não acende para clique de mouse. Quem navega por
   * teclado continua com a barra aberta enquanto lê; quem clica, não fica
   * preso.
   */
  const onFocusCapture = (event: FocusEvent<HTMLElement>) => {
    const alvo = event.target as HTMLElement | null;
    if (alvo?.matches?.(':focus-visible')) setFocused(true);
  };

  const onBlurCapture = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
  };

  const sair = async () => {
    await signOut();
    void navigate({ to: '/login' });
  };

  const shellClass = ['shell', pinned ? 'is-pinned' : '', drawerOpen ? 'is-drawer-open' : '']
    .filter(Boolean)
    .join(' ');

  const sidebarClass = [
    'sidebar',
    expanded ? 'is-expanded' : '',
    flutuando ? 'is-floating' : '',
    drawerOpen ? 'is-drawer-open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <SearchContext.Provider value={searchValue}>
      <div className={shellClass}>
        {/* Só aparece onde não há mouse: no celular e no tablet a barra é uma
            gaveta, e gaveta sem botão é gaveta que não abre. */}
        <button
          type="button"
          className="shell__menu"
          aria-label="Abrir o menu"
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(true)}
        >
          <Menu size={18} aria-hidden />
        </button>

        <div className="shell__scrim" role="presentation" onClick={() => setDrawerOpen(false)} />

        <aside
          className={sidebarClass}
          onPointerEnter={onPointerEnter}
          onPointerLeave={() => setHovered(false)}
          onFocusCapture={onFocusCapture}
          onBlurCapture={onBlurCapture}
        >
          <div className="sidebar__brand">
            <img className="sidebar__logo" src="/logo-amai.png" alt="AMAI Park" />
            {/* Recolhida, a logo é o símbolo; aberta, ganha o nome e qual sistema é. */}
            <span className="sidebar__brand-name">
              AMAI Park <em>OPS</em>
            </span>

            <button
              type="button"
              className={pinned ? 'sidebar__pin is-on' : 'sidebar__pin'}
              aria-label={pinned ? 'Desafixar menu' : 'Fixar menu'}
              aria-pressed={pinned}
              title={pinned ? 'Desafixar menu' : 'Fixar menu'}
              onClick={() => setPinned((value) => !value)}
            >
              {pinned ? <PinOff size={15} aria-hidden /> : <Pin size={15} aria-hidden />}
            </button>

            <button
              type="button"
              className="sidebar__close"
              aria-label="Fechar o menu"
              onClick={() => setDrawerOpen(false)}
            >
              <X size={17} aria-hidden />
            </button>
          </div>

          <nav className="sidebar__nav" aria-label="Módulos">
            {modulosNoMenu.map((module, index) => {
              // Título do grupo na primeira vez que ele aparece. Recolhida, a
              // barra mostra só um fio no lugar — o nome não cabe em 72px.
              const grupoNovo =
                module.group && module.group !== modulosNoMenu[index - 1]?.group
                  ? module.group
                  : null;
              const active = activeModule?.id === module.id;
              const target = module.to ?? abriveis(module.children)[0]!.to;
              const secoes = abriveis(module.children);
              /**
               * Só vira GRUPO quem tem mais de uma seção para mostrar.
               *
               * Cadastros tinha duas na configuração, mas uma delas ainda não
               * existe: sobrava "Pessoas" sozinha dentro de um grupo, e chegar
               * nela custava dois cliques — abrir Cadastros, clicar em Pessoas.
               *
               * A regra é contada por SEÇÃO ABRÍVEL, não pelo que está escrito
               * na configuração, porque a permissão faz o mesmo em qualquer
               * módulo: quem só enxerga uma seção do Financeiro via ali o
               * mesmo grupo inútil. E, no dia em que a segunda seção existir,
               * o grupo volta sozinho.
               */
              const temGrupo = secoes.length > 1;
              // Um grupo aberto por vez: o do módulo que o operador acabou de
              // abrir. Com todos abertos a barra virava uma lista de quarenta
              // linhas e o item aceso sumia no meio.
              const aberto = expanded && temGrupo && openModuleId === module.id;
              const className = ['sidebar__item', active ? 'is-active' : '']
                .filter(Boolean)
                .join(' ');

              return (
                <Fragment key={module.id}>
                  {grupoNovo && (
                    <div className="sidebar__group" aria-hidden>
                      <span className="sidebar__label">{NAV_GROUPS[grupoNovo]}</span>
                    </div>
                  )}
                  <Link
                    to={target}
                    className={className}
                    // Recolhida sobra só o ícone; sem o title o menu vira
                    // adivinhação.
                    title={expanded ? undefined : module.label}
                    aria-expanded={temGrupo ? aberto : undefined}
                    onClick={(event) => {
                      // Grupo já aberto: o clique RECOLHE e não navega. Levar
                      // para a primeira seção alguém que só queria fechar a
                      // lista é tirá-lo da tela onde ele estava trabalhando.
                      if (aberto) {
                        event.preventDefault();
                        setRecolhidoNaMao({ id: module.id, path: location.pathname });
                        return;
                      }
                      setRecolhidoNaMao(null);
                      // Navegou pela gaveta: ela fecha sozinha. Deixar o menu
                      // aberto por cima da tela que o operador acabou de pedir
                      // é o erro clássico de menu em celular.
                      setDrawerOpen(false);
                    }}
                  >
                    <module.icon size={18} strokeWidth={1.8} aria-hidden />
                    <span className="sidebar__label">{module.label}</span>
                    {temGrupo && (
                      <ChevronDown
                        size={13}
                        className={aberto ? 'sidebar__chevron is-open' : 'sidebar__chevron'}
                        aria-hidden
                      />
                    )}
                  </Link>

                  {aberto && (
                    <div
                      className="sidebar__subs"
                      role="group"
                      aria-label={`Seções de ${module.label}`}
                    >
                      {secoes.map((child) => {
                        // Prefixo só conta até a barra: sem isso
                        // `/financeiro/contas` acenderia junto com
                        // `/financeiro/contas-pagar`.
                        const naTela =
                          location.pathname === child.to ||
                          location.pathname.startsWith(`${child.to}/`);

                        return (
                          <Link
                            key={child.to}
                            to={child.to}
                            className={`sidebar__sub${naTela ? ' is-active' : ''}`}
                            aria-current={naTela ? 'page' : undefined}
                            onClick={() => setDrawerOpen(false)}
                          >
                            {child.label}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </Fragment>
              );
            })}
          </nav>
        </aside>

        <main className="workspace">
          <header className="topbar">
            <nav className="topbar__trail" aria-label="Você está em">
              {trilha.map((parte, index) => (
                <span
                  key={parte}
                  className={index === trilha.length - 1 ? 'is-current' : undefined}
                >
                  {index > 0 && <ChevronRight size={14} aria-hidden />}
                  {parte}
                </span>
              ))}
            </nav>

            <button type="button" className="topbar__search" onClick={() => setPaletteOpen(true)}>
              <Search size={15} aria-hidden />
              <span>Buscar em tudo…</span>
              <kbd className="topbar__kbd">
                <Command size={11} aria-hidden />K
              </kbd>
            </button>

            <DropdownMenu.Root>
              <DropdownMenu.Trigger
                className="topbar__user"
                aria-label={`Perfil de ${user?.name ?? ''}`}
              >
                <span className="topbar__avatar" aria-hidden>
                  {initials(user?.name ?? '?')}
                </span>
                <span className="topbar__user-name">{user?.name}</span>
                <ChevronDown size={14} aria-hidden />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
                  <div className="menu__head">
                    <div className="menu__title">{user?.name}</div>
                    <div className="menu__sub" title={perfil}>
                      {user?.fullAccess ? 'Diretor · acesso total' : perfil}
                    </div>
                  </div>
                  <DropdownMenu.Separator className="menu__sep" />
                  <DropdownMenu.Item className="menu__item" onSelect={() => void sair()}>
                    <LogOut size={15} aria-hidden />
                    Sair
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </header>
          {children}
        </main>

        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </div>
    </SearchContext.Provider>
  );
}
